<script setup>
  import {
    computed,
    onMounted,
    ref,
  } from 'vue'

  import Image from '@components/Image.vue'

  import { useAjax } from '@composables/useAjax.js'
  import { useCsrfToken } from '@composables/useCsrfToken.js'
  import { useIntlTel } from '@composables/useIntlTel.js'
  import { useRecaptcha } from '@composables/useRecaptcha.js'
  import { useI18n } from '@shared/i18n.js'
  import { generateUrl, isMobile } from '@shared/util.js'

  const props = defineProps({
    agent: Object,
    agentMemberId: String,
    propertyCode: String,
    showAgentName: Boolean,
    title: String
  })

  const TelStatus = { HIDDEN: 0, FETCHING: 1, REVEALED: 2, INQUIRED: 4 }

  const inquiryPath = ref(null)
  const submitting = ref(false)
  const tel = ref(null)
  const telStatus = ref(TelStatus.HIDDEN)
  const token = ref(null)

  const isPreviewMode = app.page.preview_mode

  const { getCsrfToken } = useCsrfToken()
  const { t, vt } = useI18n('activerecord.attributes.enquiry')

  const {
    intlCountry,
    intlNumber,
    initializeIntlTel
  } = useIntlTel("#intl_phone")

  const {
    recaptchaToken,
    initializeRecaptcha,
  } = useRecaptcha()

  onMounted(async () => {
    initializeRecaptcha('inquiry')
    initializeIntlTel()
    tel.value = props.agent.tel
    token.value = await getCsrfToken()
    observeForm()
  })

  const url = computed(() => generateUrl(`enquiry/${props.agent.id}${props.propertyCode ? `/${props.propertyCode}` : ''}`, '').pathname)

  /**
   * 電話番号クリック
   */
  const handleClickTel = () => {
    if (telStatus.value == TelStatus.HIDDEN) {
      getTel()
    }
    else {
      if ((telStatus.value & TelStatus.INQUIRED) == 0) {
        postInquiryByTel()
      }

      if (isMobile()) {
        window.open("tel:" + tel.value)
      }
    }
  }

  /**
   * submit
   */
  const handleSubmit = (e) => {
    if (isPreviewMode) {
      e.preventDefault()
      return
    }
    submitting.value = true
  }

  /**
   * 電話番号の取得
   */
  const getTel = async () => {
    if (telStatus.value == TelStatus.FETCHING) return

    const { callGet } = useAjax()
    telStatus.value = TelStatus.FETCHING
    try {
      tel.value = 'WAIT...'
      const response = await callGet(props.agent.tel_path)
      if (response.code == 'success') {
        tel.value = response.tel
        inquiryPath.value = response.inquiry_path
        telStatus.value = TelStatus.REVEALED
      }
    }
    catch (e) {
      if (e && e.response && e.response.status == 401 && e.response.data.path) {
        window.location.href = e.response.data.path
      }
    }
  }

  /**
   * フォームの表示監視
   */
  const observeForm = () => {
    const target = document.querySelector('#side_bar_main')

    // IntersectionObserverを作成
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const button = document.getElementById('fixed_enquiry_links')
        if (button) {
          if (entry.isIntersecting) {
            button.classList.add('over-form')
          } else {
            button.classList.remove('over-form')
          }
        }
      })
    }, {
      root: null,        // ビューポートを基準
      threshold: 0.1     // 10% 見えたら「見えた」と判定
    })

    observer.observe(target)
  }

  /**
   * 電話番号による問い合わせを記録
   */
  const postInquiryByTel = () => {
    telStatus.value |= TelStatus.INQUIRED
    const { callPost } = useAjax()
    callPost([inquiryPath.value, props.propertyCode].join('/'))
  }
</script>
<template>
  <div v-if="agent" id="side_bar_main">
    <div class="side_make_enquiry parts_side">
      <h3>{{ title }}</h3>
      <div class="form_enquiry">
        <div class="agency">
          <div class="logo">
            <div class="photo portrait">
              <Image :image="agent.thumbnail" />
            </div>
          </div>
          <p class="language" v-html="agent.languages"></p>
        </div>
        <div class="form">
          <form :action="url" class="form-prevent-enter-submit" accept-charset="UTF-8" method="post" @submit="handleSubmit">
            <div class="group">
              <input :placeholder="`${vt('name')}*`" maxlength="200" size="200" type="text" name="enquiry[name]" />
            </div>
            <div class="group">
              <input :placeholder="`${vt('email')}*`" maxlength="200" size="200" type="text" name="enquiry[email]" />
            </div>
            <div class="group">
              <input :placeholder="vt('phone')" id="intl_phone" maxlength="25" type="text" />
              <input v-model="intlNumber" class="intl_phone_hidden" autocomplete="off" type="hidden" name="enquiry[phone]" />
              <input v-model="intlCountry" class="intl_phone_country" autocomplete="off" type="hidden" name="enquiry[country]" />
            </div>
            <div class="group">
              <input :placeholder="`${vt('country_of_residence')}*`" maxlength="200" size="200" type="text" name="enquiry[country_of_residence]">
            </div>
            <div class="group">
              <textarea :placeholder="t('c.please_write_your_message')" maxlength="2000" rows="3" name="enquiry[body]"></textarea>
            </div>
            <div class="submit">
              <input type="hidden" name="authenticity_token" :value="token" />
              <input v-if="propertyCode" autocomplete="off" type="hidden" name="properties[]" :value="propertyCode" />
              <input autocomplete="off" type="hidden" name="enquiry[agent_id]" :value="agent.id" />
              <input autocomplete="off" type="hidden" name="enquiry[agent_member_id]" :value="agentMemberId" />
              <input id="recaptcha_token" name="recaptcha_token" type="hidden" :value="recaptchaToken" />
              <input :disabled="!recaptchaToken || submitting || isPreviewMode" :value="t('enquiries.show.js.submit')" type="submit" class="btn btn02 btn-enquiry submit-recaptcha" />
            </div>
          </form>
          <p class="text_bottom" v-html="t('enquiries.show.js.caution')"></p>
        </div>
      </div>
      <p v-if="tel" class="c-tel">
        <a @click="handleClickTel">
          <i class="fa-solid fa-phone"></i>
          <span>{{ tel }}</span>
        </a>
      </p>
    </div>
  </div>
</template>

<style scoped>
  form {
    margin-bottom: 0;
  }
</style>
